/**
 * Create from files, step one: upload 1 to 10 files and wait for each file's text (the shared `FilesPicker`), choose
 * the kind and an optional hint, Start.
 */

import { useState } from 'react';

import { ExtractionIn, extractionInHintOneMax } from '@/api/generated/schemas';
import { useStartExtraction } from '@/api/queries';
import type { AssetKind } from '@/api/types/assets';
import type { TextStatus } from '@/api/types/materials';
import { bannerError, fieldErrors, serverFieldErrors } from '@/api/validate';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { FormError } from '@/ui/StateCard';

import { KIND_HINTS, KIND_OPTIONS } from '../assetUi';
import { FilesPicker, filesProblem } from '../files/FilesPicker';
import { useMaterialUploads } from '../materials/useMaterialUploads';

/** What the person picks and types; the file ids come from the uploads. */
const TYPED = ExtractionIn.pick({ kind: true, hint: true });

/** Why Start is not possible yet, or null. */
export function startProblem(statuses: TextStatus[], kind: AssetKind | '', busy: boolean, hint = ''): string | null {
  if (statuses.length === 0) return 'Upload at least one file.';
  const files = filesProblem(statuses, busy);
  if (files) return files;
  const errors = fieldErrors(TYPED, { kind, hint }, { kind: 'Choose the kind of asset.' });
  return errors.kind ?? errors.hint ?? null;
}

export function UploadStep({ onStarted }: { onStarted: (extractionId: string) => void }) {
  const uploads = useMaterialUploads();
  const { draft, landed, statusOf } = uploads;
  const start = useStartExtraction();
  const [kind, setKind] = useState<AssetKind | ''>('');
  const [hint, setHint] = useState('');
  const problem = startProblem(landed.map((l) => statusOf(l.file)), kind, draft.busy, hint);
  const hintError = serverFieldErrors(start.error).hint;

  function submit() {
    if (problem || kind === '') return;
    start.mutate(
      { file_ids: landed.map((l) => l.file.id), kind, hint: hint.trim() || null },
      { onSuccess: (answer) => onStarted(answer.id) },
    );
  }

  return (
    <div className="card asset-form">
      <div className="card-body stack-md">
        <p className="hint">
          The AI reads the files and drafts one asset: its fields, sections and success cases, each value with the quote it
          comes from. You review everything before it is saved; the files become the asset&apos;s materials.
        </p>
        <FilesPicker uploads={uploads} id="extraction-files" />
        <div className="grid grid-2">
          <div className="field">
            <label htmlFor="extraction-kind">Kind</label>
            <select id="extraction-kind" required value={kind} aria-describedby="extraction-kind-hint" onChange={(e) => setKind(e.target.value as AssetKind)}>
              <option value="" disabled>Choose a kind</option>
              {KIND_OPTIONS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
            <p id="extraction-kind-hint" className="hint">{kind ? KIND_HINTS[kind] : 'The kind decides the sections the AI writes.'}</p>
          </div>
          <div className="field">
            <label htmlFor="extraction-hint">Hint (optional)</label>
            <textarea id="extraction-hint" rows={2} maxLength={extractionInHintOneMax} value={hint} {...fieldA11y('extraction-hint', hintError, 'extraction-hint-help')} onChange={(e) => setHint(e.target.value)} />
            <p id="extraction-hint-help" className="hint">When the files describe several assets, say which one, such as &quot;SpecGen, slides 12 to 20&quot;.</p>
            <FieldError inputId="extraction-hint" message={hintError} />
          </div>
        </div>
        <FormError error={bannerError(start.error, ['hint'])} />
        <div className="form-actions">
          {problem && <p className="hint" aria-live="polite">{problem}</p>}
          <ActionButton type="button" state={buttonState(start)} label="Start" pendingLabel="Starting…" doneLabel="Started" disabled={problem !== null} onClick={submit} />
        </div>
      </div>
    </div>
  );
}
